import { useEffect, useState } from "react";
import apiSubscription from "../../services/subscription";

/**
 * Debounced email duplicate check.
 *
 * Phase 2 split this hook to handle two independent emails:
 *   - bill_email     → checked against pkindev_platform.Bill_User
 *   - project_email  → checked against the project's user table
 *
 * They MAY hold the same value (most common case for solo owners),
 * but uniqueness is enforced separately at each layer. AccountStep
 * keeps two independent fields and feeds both into this hook so the
 * UI can show separate errors per field.
 *
 * Behavior:
 *   - Debounces 500ms after either value stops changing.
 *   - Skips a check if its email is empty or fails the basic shape
 *     test (zod owns the proper validation; this is just to avoid
 *     wasting network calls on obviously-bad input).
 *   - Cancels in-flight checks via a generation counter so stale
 *     responses can't overwrite newer state. (AbortController would
 *     also work, but the apiSubscription wrapper doesn't expose one.)
 *
 * Output shape (single source of truth for AccountStep):
 *
 *   loading: boolean              — true while either check is in flight
 *   billStatus: "new"
 *             | "existing_unverified"
 *             | "existing_verified"
 *             | null              — null until first response
 *   billMessage: string | null    — server-provided message (e.g. for unverified)
 *   billFirstName: string | null  — for existing_verified, drives login-mode UI
 *   projectError: { code, message, field } | null
 *                                 — present when project-email check fails
 *   error: string | null          — network/transport error message
 *
 * AccountStep derives its blocking decision from these:
 *   block = billStatus === "existing_unverified" || projectError !== null
 *
 * For "existing_verified" the form switches to login mode rather
 * than block (the LoginCTA + modal flow in AccountStep handles that).
 */
function useCheckEmail({
  bill_email,
  project_email,
  id_project,
  enabled = true,
  delayMs = 500,
}) {
  const [state, setState] = useState({
    loading: false,
    billStatus: null,
    billMessage: null,
    billFirstName: null,
    projectError: null,
    error: null,
  });

  useEffect(() => {
    if (!enabled) return;

    // Cheap shape gate — zod still owns the real validation. We just
    // don't want to fire requests on every keystroke before the user
    // has typed anything email-shaped.
    const billTrimmed    = (bill_email    || "").trim();
    const projectTrimmed = (project_email || "").trim();

    const billReady    = billTrimmed.includes("@");
    const projectReady = projectTrimmed.includes("@") && !!id_project;

    // If neither field is ready, reset state and bail. This matters
    // on initial mount and whenever the user clears both fields.
    if (!billReady && !projectReady) {
      setState({
        loading: false,
        billStatus: null,
        billMessage: null,
        billFirstName: null,
        projectError: null,
        error: null,
      });
      return;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      setState((s) => ({ ...s, loading: true, error: null }));

      try {
        // Fire only the checks whose inputs are ready. Each runs
        // independently — Promise.all rejects on first failure, but
        // we wrap each call so a network blip on one doesn't blank
        // out the result of the other.
        const billCall = billReady
          ? apiSubscription
              .checkBillEmail({ email: billTrimmed })
              .catch((err) => ({ __error: err }))
          : Promise.resolve(null);

        const projectCall = projectReady
          ? apiSubscription
              .checkProjectEmail({ project_email: projectTrimmed, id_project })
              .catch((err) => ({ __error: err }))
          : Promise.resolve(null);

        const [billRes, projectRes] = await Promise.all([billCall, projectCall]);

        if (cancelled) return;

        // Surface a transport-level error if either call failed at the
        // network layer (4xx/5xx come back as resolved values, not
        // rejections — Provider-Api wraps them).
        const transportError =
          billRes?.__error?.message ||
          projectRes?.__error?.message ||
          null;

        setState({
          loading: false,
          // billRes is null when bill_email wasn't ready — preserve
          // null so the UI knows there's nothing to display yet.
          billStatus:    billRes ? (billRes.status ?? null)     : null,
          billMessage:   billRes ? (billRes.message ?? null)    : null,
          billFirstName: billRes ? (billRes.first_name ?? null) : null,
          projectError:
            projectRes && projectRes.ok === false
              ? {
                  code: projectRes.code,
                  message: projectRes.message,
                  field: projectRes.field,
                }
              : null,
          error: transportError,
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          billStatus: null,
          billMessage: null,
          billFirstName: null,
          projectError: null,
          error: err?.message || "Check failed",
        });
      }
    }, delayMs);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [bill_email, project_email, id_project, enabled, delayMs]);

  return state;
}

export default useCheckEmail;